class SearchableSelectCtrl {
  constructor($element, $timeout, $scope, $document, $window) {
    'ngInject';
    this.$element = $element;
    this.$timeout = $timeout;
    this.$scope = $scope;
    this.$document = $document;
    this.$window = $window;
    this.isOpen = false;
    this.search = '';
    this.filtered = [];
    this.menuStyle = {};
    this.labelKey = 'label';
    this.idKey = 'id';
    this._optionsSignature = null;
  }

  $onInit() {
    if (!this.labelKey) this.labelKey = 'label';
    if (!this.idKey) this.idKey = 'id';
    this._docClickHandler = (event) => this.handleDocumentClick(event);
    this._repositionHandler = () => this.$scope.$applyAsync(() => this.positionMenu());
    this.$document[0].addEventListener('click', this._docClickHandler, true);
    this.refreshFiltered(true);
  }

  $onChanges(changes) {
    if (changes && changes.options) this.refreshFiltered();
  }

  $onDestroy() {
    if (this._docClickHandler) {
      this.$document[0].removeEventListener('click', this._docClickHandler, true);
    }
    this.unbindReposition();
    if (this._menuEl && this._menuEl.parentNode) {
      this._menuEl.parentNode.removeChild(this._menuEl);
    }
    this._menuEl = null;
  }

  optionsSignature() {
    return (this.options || [])
      .map((o) => `${o?.[this.idKey]}::${o?.[this.labelKey]}`)
      .join('|');
  }

  refreshFiltered(force) {
    const signature = this.optionsSignature();
    if (!force && signature === this._optionsSignature && !this._searchDirty) return;
    this._optionsSignature = signature;
    this._searchDirty = false;
    this.applyFilter();
  }

  applyFilter() {
    const labelKey = this.labelKey;
    const term = (this.search || '').trim().toLowerCase();
    let list = (this.options || []).slice();
    if (term) {
      list = list.filter((o) => String(o?.[labelKey] || '').toLowerCase().includes(term));
    }
    list.sort((a, b) =>
      String(a?.[labelKey] || '').localeCompare(String(b?.[labelKey] || ''))
    );
    this.filtered = list;
  }

  onSearchChange() {
    this._searchDirty = true;
    this.applyFilter();
  }

  handleDocumentClick(event) {
    if (!this.isOpen) return;
    if (this.$element[0].contains(event.target)) return;
    if (this._menuEl && this._menuEl.contains(event.target)) return;
    this.$scope.$apply(() => this.close());
  }

  toggle() {
    if (this.disabled) return;
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.search = '';
    this.applyFilter();
    this.isOpen = true;
    this.bindReposition();
    // Menu is rendered (ng-if) after this digest; move it to <body> so it is not
    // clipped by ancestor overflow nor offset by the modal's transformed containing block.
    this.$timeout(() => {
      this.attachMenu();
      this.positionMenu();
      const input = this._menuEl && this._menuEl.querySelector('.searchable-select-search input');
      if (input) input.focus();
    });
  }

  close() {
    this.isOpen = false;
    this.unbindReposition();
    this._menuEl = null;
  }

  attachMenu() {
    const body = this.$document[0].body;
    this._menuEl = this.$element[0].querySelector('.searchable-select-menu');
    if (this._menuEl && this._menuEl.parentNode !== body) {
      body.appendChild(this._menuEl);
    }
  }

  bindReposition() {
    this.$window.addEventListener('scroll', this._repositionHandler, true);
    this.$window.addEventListener('resize', this._repositionHandler, true);
  }

  unbindReposition() {
    if (!this._repositionHandler) return;
    this.$window.removeEventListener('scroll', this._repositionHandler, true);
    this.$window.removeEventListener('resize', this._repositionHandler, true);
  }

  positionMenu() {
    const toggle = this.$element[0].querySelector('.searchable-select-toggle');
    const menu = this._menuEl;
    if (!toggle || !menu) return;
    const rect = toggle.getBoundingClientRect();
    const scrollX = this.$window.pageXOffset;
    const scrollY = this.$window.pageYOffset;
    const viewportH = this.$window.innerHeight;
    const spaceBelow = viewportH - rect.bottom;
    const desired = 300;
    const openUp = spaceBelow < desired && rect.top > spaceBelow;
    const maxHeight = Math.max(120, Math.round((openUp ? rect.top : spaceBelow) - 8));
    const style = {
      position: 'absolute',
      left: `${Math.round(rect.left + scrollX)}px`,
      width: `${Math.round(rect.width)}px`,
      'max-height': `${maxHeight}px`,
      'z-index': 1060,
    };
    if (openUp) {
      const menuHeight = Math.min(menu.offsetHeight || maxHeight, maxHeight);
      style.top = `${Math.round(rect.top + scrollY - menuHeight - 2)}px`;
    } else {
      style.top = `${Math.round(rect.bottom + scrollY + 2)}px`;
    }
    this.menuStyle = style;
  }

  hasSelection() {
    if (this.valueKey) return this.model !== null && this.model !== undefined && this.model !== '';
    return !!(this.model && Object.keys(this.model).length);
  }

  displayLabel() {
    if (!this.hasSelection()) return this.placeholder || 'Select…';
    if (this.valueKey) {
      const opt = (this.options || []).find(
        (o) => String(o[this.valueKey]) === String(this.model)
      );
      return opt ? opt[this.labelKey] : this.placeholder || 'Select…';
    }
    return this.model[this.labelKey];
  }

  isSelected(option) {
    if (this.valueKey) {
      return this.model != null && String(option[this.valueKey]) === String(this.model);
    }
    if (!this.model) return false;
    if (option[this.idKey] != null && this.model[this.idKey] != null) {
      return String(option[this.idKey]) === String(this.model[this.idKey]);
    }
    return option === this.model;
  }

  select(option) {
    this.model = this.valueKey ? option[this.valueKey] : option;
    this.close();
    this.$timeout(() => this.onChange());
  }

  clear() {
    this.model = this.valueKey ? '' : null;
    this.close();
    this.$timeout(() => this.onChange());
  }

  onSearchKeyDown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.filtered.length) this.select(this.filtered[0]);
    }
  }
}

export default {
  bindings: {
    options: '<',
    model: '=',
    labelKey: '@',
    valueKey: '@',
    idKey: '@',
    placeholder: '@',
    disabled: '<',
    size: '@',
    allowClear: '<',
    clearLabel: '@',
    onChange: '&',
  },
  templateUrl:
    'form-builder/build-wizard/searchable-select/searchable-select.component.html',
  controller: SearchableSelectCtrl,
};
